import {t, useLanguage, interfaceLocale} from '../../i18n';
import React, { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const moneyFormatter = () => new Intl.NumberFormat(interfaceLocale(), {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function cents(value) {
  const n = Number(value);
  return Number.isSafeInteger(n) ? n : 0;
}

function moneyFromCents(value) {
  return moneyFormatter().format(cents(value) / 100);
}

function toIso(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
}

function monthLabel(value) {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}$/.test(raw)) return raw || "Mes";
  const date = new Date(`${raw}-01T12:00:00.000Z`);
  return date
    .toLocaleDateString(interfaceLocale(), { month: "short", year: "numeric", timeZone: "UTC" })
    .replace(".", "");
}

function shortDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || !Number.isFinite(date.getTime())) return "—";
  return date.toLocaleDateString(interfaceLocale(), {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).replace(".", "");
}

function paymentKindLabel(value) {
  if (value === "membership_initial") return t("Compra inicial");
  if (value === "membership_renewal") return t("Renovación");
  return t("Membresía");
}

function initials(value) {
  const words = String(value || "Cliente")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = words[0]?.[0] || "C";
  const second = words.length > 1 ? words[words.length - 1]?.[0] || "" : "";
  return `${first}${second}`.toUpperCase();
}

function statusClass(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "active") return "is-active";
  if (key === "paused") return "is-paused";
  if (key === "cancelled") return "is-cancelled";
  if (key === "expired") return "is-expired";
  return "is-neutral";
}

function nextChargeLabel(value) {
  return value ? shortDate(value) : t("Sin próximo cobro");
}

function compactTransactionId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const parts = raw.split("-").filter(Boolean);
  if (parts.length >= 3) return `${parts[0]}-…-${parts[parts.length - 1]}`;
  if (raw.length <= 18) return raw;
  return `${raw.slice(0, 8)}…${raw.slice(-6)}`;
}

export default function MembershipCollectionsPanel({
  db,
  businessId,
  startDate = null,
  endDate = null,
  periodLabel = "",
  enabled = true,
}) {
  useLanguage();
  const startIso = useMemo(() => toIso(startDate), [startDate]);
  const endIso = useMemo(() => toIso(endDate), [endDate]);

  const [state, setState] = useState({
    status: "idle",
    report: null,
    error: "",
  });
  const [expandedSubscriptionId, setExpandedSubscriptionId] = useState("");
  const [copiedTransactionId, setCopiedTransactionId] = useState("");

  useEffect(() => {
    let cancelled = false;

    if (!enabled || !businessId) {
      setState((current) => ({ ...current, status: "idle" }));
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      setState((current) => ({ ...current, status: "loading", error: "" }));

      try {
        const functions = getFunctions(db?.app, "us-central1");
        const callable = httpsCallable(
          functions,
          "getBusinessMembershipFinancialReportV1",
        );

        const payload = {
          businessId,
          ...(startIso ? { startIso } : {}),
          ...(endIso ? { endIso } : {}),
        };

        const response = await callable(payload);
        const data = response?.data || {};

        if (!data?.ok || !data?.report) {
          throw new Error("Loop no devolvió un reporte financiero válido.");
        }

        if (data.report.pAndLIncluded !== false) {
          throw new Error("El reporte de cobros perdió su separación contable.");
        }

        if (data.report?.totals?.settlementStatus !== "not_available_until_stage4") {
          throw new Error("El estado de liquidaciones no coincide con la etapa financiera actual.");
        }

        if (!cancelled) {
          setState({
            status: "ready",
            report: data.report,
            error: "",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: "error",
            report: null,
            error: error?.message || String(error),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, businessId, enabled, startIso, endIso]);

  useEffect(() => {
    setExpandedSubscriptionId("");
    setCopiedTransactionId("");
  }, [businessId, startIso, endIso]);

  const toggleExpandedClient = (rowKey) => {
    if (!rowKey) return;
    setExpandedSubscriptionId((current) => (current === rowKey ? "" : rowKey));
  };

  const copyTransactionId = async (transactionId) => {
    const value = String(transactionId || "").trim();
    if (!value || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedTransactionId(value);
      window.setTimeout(() => {
        setCopiedTransactionId((current) => (current === value ? "" : current));
      }, 1600);
    } catch (_) {
      setCopiedTransactionId("");
    }
  };

  if (!enabled) return null;

  const report = state.report;
  const totals = report?.totals || {};
  const monthly = Array.isArray(report?.monthlyTrend) ? report.monthlyTrend : [];
  const transactions = Array.isArray(report?.transactions) ? report.transactions : [];
  const clientOverview = Array.isArray(report?.clientOverview) ? report.clientOverview : [];
  const clientOverviewReady = report?.clientOverviewStatus === "ready";

  const enrichedRecentPayments = clientOverview
    .flatMap((client) =>
      (Array.isArray(client?.transactions) ? client.transactions : []).map((transaction) => ({
        ...transaction,
        customerName: client?.customerName || "Cliente",
        planName: client?.planName || "Membresía",
      })),
    )
    .sort((left, right) =>
      String(right?.occurredAtIso || "").localeCompare(String(left?.occurredAtIso || "")),
    );

  const recentPayments = enrichedRecentPayments.length
    ? enrichedRecentPayments.slice(0, 6)
    : transactions.slice(0, 6).map((transaction) => ({
        ...transaction,
        customerName: "Cliente",
        planName: "Membresía",
      }));

  return (
    <article className="loop-membership-finance" data-loop-membership-finance="a3p2-v1">
      <style>{`
        .loop-membership-finance{
          grid-column:1 / -1;
          min-width:0;
          border:1px solid #dfe8eb;
          border-radius:16px;
          background:#fff;
          padding:18px;
          box-shadow:0 8px 28px rgba(15,23,42,.035);
        }
        .loop-membership-finance *{box-sizing:border-box}
        .loop-membership-finance-head{
          display:flex;
          justify-content:space-between;
          gap:16px;
          align-items:flex-start;
          margin-bottom:14px;
        }
        .loop-membership-finance-head span{
          display:block;
          color:#0f766e;
          font-size:10px;
          font-weight:900;
          letter-spacing:.08em;
          text-transform:uppercase;
          margin-bottom:4px;
        }
        .loop-membership-finance-head h4{
          margin:0;
          color:#0f172a;
          font-size:17px;
          line-height:1.2;
        }
        .loop-membership-finance-head p{
          margin:6px 0 0;
          max-width:680px;
          color:#64748b;
          font-size:11px;
          line-height:1.45;
        }
        .loop-membership-finance-period{
          flex:0 0 auto;
          padding:7px 10px;
          border:1px solid #dbe7ea;
          border-radius:999px;
          background:#f8fafc;
          color:#475569;
          font-size:10px;
          font-weight:850;
          white-space:nowrap;
        }
        .loop-membership-finance-kpis{
          display:grid;
          grid-template-columns:repeat(5,minmax(0,1fr));
          gap:9px;
        }
        .loop-membership-finance-kpi{
          min-width:0;
          padding:12px;
          border:1px solid #edf1f3;
          border-radius:13px;
          background:#fbfcfd;
        }
        .loop-membership-finance-kpi span{
          display:block;
          color:#64748b;
          font-size:9px;
          font-weight:800;
          margin-bottom:5px;
        }
        .loop-membership-finance-kpi strong{
          display:block;
          color:#0f172a;
          font-size:18px;
          line-height:1.05;
        }
        .loop-membership-finance-kpi em{
          display:block;
          margin-top:5px;
          color:#94a3b8;
          font-size:9px;
          font-style:normal;
          line-height:1.3;
        }
        .loop-membership-finance-grid{
          display:grid;
          grid-template-columns:minmax(0,.92fr) minmax(340px,1.08fr);
          gap:12px;
          margin-top:12px;
        }
        .loop-membership-finance-block,
        .loop-membership-client-block{
          min-width:0;
          border:1px solid #edf1f3;
          border-radius:13px;
          padding:13px;
          background:#fff;
        }
        .loop-membership-finance-block-head,
        .loop-membership-client-head{
          display:flex;
          justify-content:space-between;
          gap:10px;
          align-items:flex-start;
          margin-bottom:9px;
        }
        .loop-membership-finance-block-head strong,
        .loop-membership-client-head strong{
          color:#0f172a;
          font-size:12px;
        }
        .loop-membership-finance-block-head span,
        .loop-membership-client-head span{
          color:#94a3b8;
          font-size:9px;
          font-weight:800;
        }
        .loop-membership-client-head p{
          margin:4px 0 0;
          color:#64748b;
          font-size:10px;
          line-height:1.4;
        }
        .loop-membership-finance-months,
        .loop-membership-finance-recent{
          display:grid;
          gap:7px;
        }
        .loop-membership-finance-month{
          display:grid;
          grid-template-columns:minmax(95px,.8fr) repeat(3,minmax(74px,.7fr));
          align-items:center;
          gap:10px;
          border-top:1px solid #f1f5f9;
          padding-top:8px;
        }
        .loop-membership-finance-month:first-child{
          border-top:0;
          padding-top:0;
        }
        .loop-membership-finance-month div{min-width:0}
        .loop-membership-finance-month span{
          display:block;
          color:#94a3b8;
          font-size:8px;
          font-weight:800;
          margin-bottom:2px;
        }
        .loop-membership-finance-month strong{
          display:block;
          color:#334155;
          font-size:10.5px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .loop-membership-recent-row{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto auto;
          align-items:center;
          gap:12px;
          border-top:1px solid #f1f5f9;
          padding-top:9px;
        }
        .loop-membership-recent-row:first-child{
          border-top:0;
          padding-top:0;
        }
        .loop-membership-recent-person{
          display:flex;
          align-items:center;
          min-width:0;
          gap:9px;
        }
        .loop-membership-avatar{
          width:30px;
          height:30px;
          flex:0 0 30px;
          display:grid;
          place-items:center;
          border-radius:50%;
          background:#ecfdf5;
          color:#047857;
          font-size:9px;
          font-weight:900;
          letter-spacing:.02em;
        }
        .loop-membership-recent-person-text{min-width:0}
        .loop-membership-recent-person-text strong{
          display:block;
          color:#0f172a;
          font-size:10.5px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .loop-membership-recent-person-text span{
          display:block;
          color:#94a3b8;
          font-size:8.5px;
          margin-top:2px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .loop-membership-recent-type{
          min-width:0;
          text-align:right;
        }
        .loop-membership-recent-type span{
          display:block;
          color:#475569;
          font-size:9px;
          font-weight:800;
          white-space:nowrap;
        }
        .loop-membership-recent-money{
          min-width:72px;
          text-align:right;
        }
        .loop-membership-recent-money strong{
          display:block;
          color:#0f172a;
          font-size:10.5px;
        }
        .loop-membership-recent-money span{
          display:block;
          color:#64748b;
          font-size:8px;
          margin-top:2px;
        }
        .loop-membership-client-block{
          margin-top:12px;
          padding:0;
          overflow:hidden;
        }
        .loop-membership-client-head{
          padding:13px 14px 11px;
          margin:0;
          border-bottom:1px solid #edf1f3;
          background:#fbfcfd;
        }
        .loop-membership-client-table-scroll{
          overflow-x:auto;
          width:100%;
        }
        .loop-membership-client-table{
          min-width:1120px;
        }
        .loop-membership-client-columns,
        .loop-membership-client-row{
          display:grid;
          grid-template-columns:
            minmax(145px,1.25fr)
            minmax(130px,1fr)
            82px
            96px
            105px
            88px
            82px
            92px
            108px
            34px;
          gap:10px;
          align-items:center;
        }
        .loop-membership-client-columns{
          padding:8px 12px;
          color:#94a3b8;
          background:#fff;
          border-bottom:1px solid #f1f5f9;
          font-size:7.5px;
          font-weight:900;
          letter-spacing:.04em;
          text-transform:uppercase;
        }
        .loop-membership-client-row{
          padding:10px 12px;
          border-bottom:1px solid #f1f5f9;
          color:#334155;
          font-size:9.5px;
          cursor:pointer;
          outline:none;
        }
        .loop-membership-client-item:last-child .loop-membership-client-row{
          border-bottom:0;
        }
        .loop-membership-client-row:hover,
        .loop-membership-client-row:focus-visible{
          background:#f8fbfb;
        }
        .loop-membership-client-row:focus-visible{
          box-shadow:inset 0 0 0 2px #99f6e4;
        }
        .loop-membership-client-primary{
          min-width:0;
          display:flex;
          align-items:center;
          gap:8px;
        }
        .loop-membership-client-primary strong,
        .loop-membership-client-plan strong{
          display:block;
          min-width:0;
          color:#0f172a;
          font-size:10px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .loop-membership-client-plan span,
        .loop-membership-client-primary span{
          display:block;
          color:#94a3b8;
          font-size:8px;
          margin-top:2px;
        }
        .loop-membership-status{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          width:max-content;
          min-width:58px;
          padding:4px 7px;
          border-radius:999px;
          font-size:8px;
          font-weight:900;
          line-height:1;
        }
        .loop-membership-status.is-active{background:#ecfdf5;color:#047857}
        .loop-membership-status.is-paused{background:#fffbeb;color:#a16207}
        .loop-membership-status.is-cancelled{background:#fff7ed;color:#c2410c}
        .loop-membership-status.is-expired{background:#f1f5f9;color:#64748b}
        .loop-membership-status.is-neutral{background:#f8fafc;color:#64748b}
        .loop-membership-client-number{
          color:#0f172a;
          font-weight:800;
          white-space:nowrap;
        }
        .loop-membership-client-muted{
          color:#64748b;
          white-space:nowrap;
        }
        .loop-membership-client-next{
          color:#0f766e;
          font-weight:800;
          white-space:nowrap;
        }
        .loop-membership-expand-cell{
          min-width:0;
          display:flex;
          align-items:center;
          justify-content:flex-end;
          gap:8px;
        }
        .loop-membership-expand-label{
          display:none;
          color:#0f766e;
          font-size:9px;
          font-weight:850;
        }
        .loop-membership-expand-button{
          width:28px;
          height:28px;
          display:grid;
          place-items:center;
          border:1px solid #dfe8eb;
          border-radius:8px;
          background:#fff;
          color:#475569;
          font-size:14px;
          line-height:1;
          transition:transform .18s ease,background .18s ease;
          pointer-events:none;
        }
        .loop-membership-client-row:hover .loop-membership-expand-button,
        .loop-membership-client-row:focus-visible .loop-membership-expand-button{
          background:#f8fafc;
        }
        .loop-membership-expand-button.is-open{
          transform:rotate(180deg);
        }
        .loop-membership-history{
          border-bottom:1px solid #f1f5f9;
          background:#f8fafc;
          padding:10px 14px 12px;
        }
        .loop-membership-history-head{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:10px;
          margin-bottom:7px;
        }
        .loop-membership-history-head strong{
          color:#334155;
          font-size:9.5px;
        }
        .loop-membership-history-head span{
          color:#94a3b8;
          font-size:8px;
          font-weight:800;
        }
        .loop-membership-history-columns,
        .loop-membership-history-row{
          display:grid;
          grid-template-columns:
            minmax(100px,.85fr)
            minmax(120px,1.05fr)
            minmax(90px,.8fr)
            minmax(90px,.8fr)
            minmax(100px,.85fr)
            minmax(220px,1.65fr);
          gap:14px;
          align-items:center;
        }
        .loop-membership-history-columns{
          color:#94a3b8;
          font-size:7px;
          font-weight:900;
          text-transform:uppercase;
          padding:5px 0;
        }
        .loop-membership-history-row{
          min-height:28px;
          color:#475569;
          font-size:8.5px;
          border-top:1px solid #e8eef1;
        }
        .loop-membership-history-tx{
          min-width:0;
          display:flex;
          align-items:center;
          gap:7px;
        }
        .loop-membership-history-tx code{
          min-width:0;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
          font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;
          color:#64748b;
          font-size:8.2px;
        }
        .loop-membership-copy-tx{
          flex:0 0 auto;
          border:1px solid #dfe8eb;
          border-radius:7px;
          background:#fff;
          color:#475569;
          padding:4px 7px;
          font-size:7.5px;
          font-weight:800;
          cursor:pointer;
        }
        .loop-membership-copy-tx:hover{
          background:#f1f5f9;
        }
        .loop-membership-finance-empty,
        .loop-membership-finance-loading,
        .loop-membership-finance-error{
          padding:14px;
          border-radius:12px;
          font-size:11px;
          line-height:1.45;
        }
        .loop-membership-finance-empty,
        .loop-membership-finance-loading{
          color:#64748b;
          background:#f8fafc;
        }
        .loop-membership-finance-error{
          color:#991b1b;
          background:#fef2f2;
        }
        .loop-membership-client-message{
          margin:12px;
        }
        .loop-membership-finance-note{
          margin-top:12px;
          padding-top:10px;
          border-top:1px solid #eef2f7;
          color:#64748b;
          font-size:9.5px;
          line-height:1.45;
        }
        .loop-membership-finance-note strong{
          color:#475569;
        }
        @media(max-width:1080px){
          .loop-membership-finance-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}
          .loop-membership-finance-grid{grid-template-columns:1fr}
        }
        @media(max-width:600px){
          .loop-membership-finance{
            padding:14px;
            scroll-margin-top:76px;
          }
          .loop-membership-finance-head{
            display:grid;
            gap:10px;
          }
          .loop-membership-finance-head h4{
            font-size:16px;
            line-height:1.25;
          }
          .loop-membership-finance-period{justify-self:start}
          .loop-membership-finance-kpis{grid-template-columns:1fr 1fr}
          .loop-membership-finance-kpi-payable{grid-column:1 / -1}
          .loop-membership-finance-month{
            grid-template-columns:minmax(90px,1fr) 1fr;
          }
          .loop-membership-finance-month .mobile-hide{display:none}
          .loop-membership-recent-row{
            grid-template-columns:minmax(0,1fr) auto;
            gap:7px 10px;
          }
          .loop-membership-recent-type{
            display:block;
            grid-column:1 / -1;
            grid-row:2;
            padding-left:39px;
            text-align:left;
            margin-top:-4px;
          }
          .loop-membership-recent-type span{
            color:#64748b;
            font-size:8px;
            font-weight:750;
          }
          .loop-membership-client-head>span{
            white-space:nowrap;
          }
          .loop-membership-client-table-scroll{
            overflow:visible;
          }
          .loop-membership-client-table{
            min-width:0;
            padding:10px;
          }
          .loop-membership-client-columns{
            display:none;
          }
          .loop-membership-client-item{
            border:1px solid #e8eef1;
            border-radius:13px;
            overflow:hidden;
            background:#fff;
            margin-bottom:10px;
          }
          .loop-membership-client-item:last-child{
            margin-bottom:0;
          }
          .loop-membership-client-row{
            grid-template-columns:minmax(0,1fr) auto;
            grid-template-areas:
              "primary status"
              "plan plan"
              "last next"
              "gross fee"
              "settle type"
              "expand expand";
            gap:10px 12px;
            padding:12px;
            border-bottom:0;
            background:#fff;
          }
          .loop-membership-client-row[aria-expanded="true"]{
            border-bottom:1px solid #edf1f3;
          }
          .loop-membership-client-row>div:nth-child(1){grid-area:primary}
          .loop-membership-client-row>div:nth-child(2){
            grid-area:plan;
            padding:8px 0 2px 38px;
            border-top:1px solid #f1f5f9;
          }
          .loop-membership-client-row>div:nth-child(3){
            grid-area:status;
            justify-self:end;
          }
          .loop-membership-client-row>div:nth-child(4){grid-area:last}
          .loop-membership-client-row>div:nth-child(5){grid-area:type}
          .loop-membership-client-row>div:nth-child(6){grid-area:gross}
          .loop-membership-client-row>div:nth-child(7){grid-area:fee}
          .loop-membership-client-row>div:nth-child(8){grid-area:settle}
          .loop-membership-client-row>div:nth-child(9){grid-area:next}
          .loop-membership-client-row>div:nth-child(10){grid-area:expand}
          .loop-membership-client-row>div:nth-child(4),
          .loop-membership-client-row>div:nth-child(5),
          .loop-membership-client-row>div:nth-child(6),
          .loop-membership-client-row>div:nth-child(7),
          .loop-membership-client-row>div:nth-child(8),
          .loop-membership-client-row>div:nth-child(9){
            min-width:0;
            white-space:normal;
          }
          .loop-membership-client-row>div:nth-child(4)::before,
          .loop-membership-client-row>div:nth-child(5)::before,
          .loop-membership-client-row>div:nth-child(6)::before,
          .loop-membership-client-row>div:nth-child(7)::before,
          .loop-membership-client-row>div:nth-child(8)::before,
          .loop-membership-client-row>div:nth-child(9)::before{
            display:block;
            margin-bottom:3px;
            color:#94a3b8;
            font-size:7px;
            font-weight:900;
            letter-spacing:.035em;
            text-transform:uppercase;
          }
          .loop-membership-client-row>div:nth-child(4)::before{content:"Último cobro"}
          .loop-membership-client-row>div:nth-child(5)::before{content:"Tipo"}
          .loop-membership-client-row>div:nth-child(6)::before{content:"Cobrado"}
          .loop-membership-client-row>div:nth-child(7)::before{content:"Fee Loop"}
          .loop-membership-client-row>div:nth-child(8)::before{content:"A liquidar"}
          .loop-membership-client-row>div:nth-child(9)::before{content:"Próximo cobro"}
          .loop-membership-client-number,
          .loop-membership-client-muted,
          .loop-membership-client-next{
            font-size:10px;
          }
          .loop-membership-expand-cell{
            justify-content:space-between;
            padding-top:9px;
            border-top:1px solid #f1f5f9;
          }
          .loop-membership-expand-label{
            display:block;
          }
          .loop-membership-history{
            padding:10px 12px 12px;
            border-bottom:0;
          }
          .loop-membership-history-columns{
            display:none;
          }
          .loop-membership-history-row{
            display:grid;
            grid-template-columns:1fr 1fr;
            gap:10px 12px;
            min-height:0;
            padding:10px 0;
            border-top:1px solid #e8eef1;
          }
          .loop-membership-history-row>div{
            min-width:0;
          }
          .loop-membership-history-row>div::before{
            display:block;
            margin-bottom:3px;
            color:#94a3b8;
            font-size:7px;
            font-weight:900;
            letter-spacing:.035em;
            text-transform:uppercase;
          }
          .loop-membership-history-row>div:nth-child(1)::before{content:"Fecha"}
          .loop-membership-history-row>div:nth-child(2)::before{content:"Tipo"}
          .loop-membership-history-row>div:nth-child(3)::before{content:"Cobrado"}
          .loop-membership-history-row>div:nth-child(4)::before{content:"Fee Loop"}
          .loop-membership-history-row>div:nth-child(5)::before{content:"A liquidar"}
          .loop-membership-history-row>div:nth-child(6)::before{content:"Tx ID"}
          .loop-membership-history-tx{
            display:grid;
            grid-template-columns:minmax(0,1fr) auto;
            gap:6px;
          }
          .loop-membership-history-tx::before{
            grid-column:1 / -1;
          }
          .loop-membership-history-tx code{
            align-self:center;
          }
          .loop-membership-copy-tx{
            min-height:28px;
          }
        }
      `}</style>

      <div className="loop-membership-finance-head">
        <div>
          <span>{t("Cobros por membresías")}</span>
          <h4>{t("Dinero recibido por las membresías de tus clientes")}</h4>
          <p>
            {t("Compras iniciales y renovaciones procesadas por Loop. Aquí ves el dinero efectivamente cobrado, quién pagó y cuánto corresponde liquidar al negocio.")}
          </p>
        </div>
        <div className="loop-membership-finance-period">{periodLabel || t("Período seleccionado")}</div>
      </div>

      {state.status === "loading" && !report ? (
        <div className="loop-membership-finance-loading">{t("Cargando cobros de membresías…")}</div>
      ) : state.status === "error" ? (
        <div className="loop-membership-finance-error">{t("No pudimos cargar los cobros de membresías.")}{t(state.error)}
        </div>
      ) : report ? (
        <>
          <div className="loop-membership-finance-kpis">
            <div className="loop-membership-finance-kpi">
              <span>{t("Cobrado bruto")}</span>
              <strong>{moneyFromCents(totals.grossCollectedCents)}</strong>
              <em>{t(cents(totals.transactionCount) === 1 ? "{count} transacción aprobada" : "{count} transacciones aprobadas", {count:cents(totals.transactionCount)})}</em>
            </div>
            <div className="loop-membership-finance-kpi">
              <span>{t("Compras iniciales")}</span>
              <strong>{moneyFromCents(totals.initialGrossCents)}</strong>
              <em>{t("Altas nuevas cobradas")}</em>
            </div>
            <div className="loop-membership-finance-kpi">
              <span>{t("Renovaciones")}</span>
              <strong>{moneyFromCents(totals.renewalGrossCents)}</strong>
              <em>{t("Renovaciones aprobadas")}</em>
            </div>
            <div className="loop-membership-finance-kpi">
              <span>{t("Fee Loop")}</span>
              <strong>{moneyFromCents(totals.loopFeeCents)}</strong>
              <em>{t("Fee de procesamiento Loop")}</em>
            </div>
            <div className="loop-membership-finance-kpi loop-membership-finance-kpi-payable">
              <span>{t("Monto a liquidar")}</span>
              <strong>{moneyFromCents(totals.amountToSettleCents)}</strong>
              <em>{moneyFromCents(totals.grossCollectedCents)} − {moneyFromCents(totals.loopFeeCents)}</em>
            </div>
          </div>

          <div className="loop-membership-finance-grid">
            <section className="loop-membership-finance-block">
              <div className="loop-membership-finance-block-head">
                <strong>{t("Cobros por mes")}</strong>
                <span>{t(monthly.length === 1 ? "{count} mes" : "{count} meses", {count:monthly.length})}</span>
              </div>

              {monthly.length ? (
                <div className="loop-membership-finance-months">
                  {monthly.map((row) => (
                    <div className="loop-membership-finance-month" key={row.month}>
                      <div>
                        <span>{t("MES")}</span>
                        <strong>{monthLabel(row.month)}</strong>
                      </div>
                      <div>
                        <span>{t("COBRADO")}</span>
                        <strong>{moneyFromCents(row.grossCollectedCents)}</strong>
                      </div>
                      <div className="mobile-hide">
                        <span>{t("INICIAL / RENOVACIÓN")}</span>
                        <strong>
                          {moneyFromCents(row.initialGrossCents)} / {moneyFromCents(row.renewalGrossCents)}
                        </strong>
                      </div>
                      <div>
                        <span>{t("A LIQUIDAR")}</span>
                        <strong>{moneyFromCents(row.amountToSettleCents)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="loop-membership-finance-empty">{t("No hay cobros de membresías aprobados dentro de este período.")}</div>
              )}
            </section>

            <section className="loop-membership-finance-block">
              <div className="loop-membership-finance-block-head">
                <strong>{t("Últimos cobros")}</strong>
                <span>{recentPayments.length}{" " + t("reciente")}{recentPayments.length === 1 ? "" : "s"}</span>
              </div>

              {recentPayments.length ? (
                <div className="loop-membership-finance-recent">
                  {recentPayments.map((row) => (
                    <div
                      className="loop-membership-recent-row"
                      key={`${row.providerTransactionId || row.paymentAttemptId || row.occurredAtIso}`}
                    >
                      <div className="loop-membership-recent-person">
                        <div className="loop-membership-avatar">{initials(row.customerName)}</div>
                        <div className="loop-membership-recent-person-text">
                          <strong>{row.customerName || t("Cliente")}</strong>
                          <span>{row.planName || t("Membresía")}</span>
                        </div>
                      </div>
                      <div className="loop-membership-recent-type">
                        <span>{shortDate(row.occurredAtIso)} · {paymentKindLabel(row.paymentKind)}</span>
                      </div>
                      <div className="loop-membership-recent-money">
                        <strong>{moneyFromCents(row.grossCollectedCents)}</strong>
                        <span>{t("A liquidar")}{moneyFromCents(row.amountToSettleCents)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="loop-membership-finance-empty">{t("Sin cobros aprobados en el rango.")}</div>
              )}
            </section>
          </div>

          <section className="loop-membership-client-block">
            <div className="loop-membership-client-head">
              <div>
                <strong>{t("Detalle por cliente")}</strong>
                <p>{t("Una fila por membresía. Expande una fila para ver su historial de cobros.")}</p>
              </div>
              <span>
                {clientOverview.length}{" " + t("cliente")}{clientOverview.length === 1 ? "" : "s"}
              </span>
            </div>

            {!clientOverviewReady ? (
              <div className="loop-membership-finance-empty loop-membership-client-message">{t("El detalle por cliente no está disponible para este rango.")}{report?.clientOverviewReason ? ` ${report.clientOverviewReason}` : ""}
              </div>
            ) : clientOverview.length ? (
              <div className="loop-membership-client-table-scroll">
                <div className="loop-membership-client-table">
                  <div className="loop-membership-client-columns" aria-hidden="true">
                    <div>{t("Cliente")}</div>
                    <div>{t("Plan")}</div>
                    <div>{t("Estado")}</div>
                    <div>{t("Último cobro")}</div>
                    <div>{t("Tipo")}</div>
                    <div>{t("Cobrado")}</div>
                    <div>{t("Fee Loop")}</div>
                    <div>{t("A liquidar")}</div>
                    <div>{t("Próximo cobro")}</div>
                    <div />
                  </div>

                  {clientOverview.map((row) => {
                    const rowKey = row.subscriptionId || row.membershipId || row.customerId;
                    const isExpanded = expandedSubscriptionId === rowKey;
                    const history = Array.isArray(row.transactions) ? row.transactions : [];

                    return (
                      <div className="loop-membership-client-item" key={rowKey}>
                        <div
                          className="loop-membership-client-row"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? "Ocultar" : "Ver"} historial de ${row.customerName || "cliente"}`}
                          onClick={() => toggleExpandedClient(rowKey)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              toggleExpandedClient(rowKey);
                            }
                          }}
                        >
                          <div className="loop-membership-client-primary">
                            <div className="loop-membership-avatar">{initials(row.customerName)}</div>
                            <div>
                              <strong>{row.customerName || t("Cliente")}</strong>
                              <span>{cents(row.transactionCount)}{" " + t("cobro")}{cents(row.transactionCount) === 1 ? "" : "s"}</span>
                            </div>
                          </div>
                          <div className="loop-membership-client-plan">
                            <strong>{row.planName || t("Membresía")}</strong>
                            <span>{row.subscriptionId ? "Suscripción activa en Loop" : t("Membresía")}</span>
                          </div>
                          <div>
                            <span className={`loop-membership-status ${statusClass(row.membershipStatus)}`}>
                              {row.membershipStatusLabel || "—"}
                            </span>
                          </div>
                          <div className="loop-membership-client-muted">{shortDate(row.lastChargeAt)}</div>
                          <div className="loop-membership-client-muted">{paymentKindLabel(row.lastPaymentKind)}</div>
                          <div className="loop-membership-client-number">{moneyFromCents(row.grossCollectedCents)}</div>
                          <div className="loop-membership-client-number">{moneyFromCents(row.loopFeeCents)}</div>
                          <div className="loop-membership-client-number">{moneyFromCents(row.amountToSettleCents)}</div>
                          <div className="loop-membership-client-next">{nextChargeLabel(row.nextBillingAt)}</div>
                          <div className="loop-membership-expand-cell">
                            <span className="loop-membership-expand-label">
                              {isExpanded ? "Ocultar historial de cobros" : "Ver historial de cobros"}
                            </span>
                            <span
                              className={`loop-membership-expand-button ${isExpanded ? "is-open" : ""}`}
                              aria-hidden="true"
                            >
                              ⌄
                            </span>
                          </div>
                        </div>

                        {isExpanded ? (
                          <div className="loop-membership-history">
                            <div className="loop-membership-history-head">
                              <strong>{t("Historial de cobros ·") + " "}{row.customerName || t("Cliente")}</strong>
                              <span>{history.length}{" " + t("movimiento")}{history.length === 1 ? "" : "s"}</span>
                            </div>
                            <div className="loop-membership-history-columns" aria-hidden="true">
                              <div>{t("Fecha")}</div>
                              <div>{t("Tipo")}</div>
                              <div>{t("Cobrado")}</div>
                              <div>{t("Fee Loop")}</div>
                              <div>{t("A liquidar")}</div>
                              <div>Tx ID</div>
                            </div>
                            {history.map((transaction) => (
                              <div
                                className="loop-membership-history-row"
                                key={transaction.providerTransactionId || transaction.paymentAttemptId}
                              >
                                <div>{shortDate(transaction.occurredAtIso)}</div>
                                <div>{paymentKindLabel(transaction.paymentKind)}</div>
                                <div className="loop-membership-client-number">
                                  {moneyFromCents(transaction.grossCollectedCents)}
                                </div>
                                <div>{moneyFromCents(transaction.loopFeeCents)}</div>
                                <div>{moneyFromCents(transaction.amountToSettleCents)}</div>
                                <div className="loop-membership-history-tx">
                                  <code title={transaction.providerTransactionId || ""}>
                                    {compactTransactionId(transaction.providerTransactionId)}
                                  </code>
                                  {transaction.providerTransactionId ? (
                                    <button
                                      type="button"
                                      className="loop-membership-copy-tx"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        copyTransactionId(transaction.providerTransactionId);
                                      }}
                                      title={t("Copiar Tx ID completo")}
                                    >
                                      {copiedTransactionId === transaction.providerTransactionId ? "Copiado" : t("Copiar")}
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="loop-membership-finance-empty loop-membership-client-message">{t("No hay clientes con cobros de membresías aprobados dentro de este período.")}</div>
            )}
          </section>

          <div className="loop-membership-finance-note">
            <strong>{t("Monto a liquidar = cobrado bruto − Fee Loop.")}</strong>{" "}{t("Para este período:")}{" "}{moneyFromCents(totals.grossCollectedCents)} − {moneyFromCents(totals.loopFeeCents)}
            {" = "}{moneyFromCents(totals.amountToSettleCents)}{t(". Todavía no significa “liquidado”: la conciliación de transferencias al negocio se incorporará en la etapa de liquidaciones. Estos cobros se muestran aparte del P&L para no duplicar el ingreso de membresías reconocido por consumo.")}
          </div>
        </>
      ) : (
        <div className="loop-membership-finance-empty">{t("Todavía no hay información financiera de membresías para mostrar.")}</div>
      )}
    </article>
  );
}
